import 'regenerator-runtime/runtime';
import qs from 'query-string';
import React from 'react';
import ReactDOM from 'react-dom';
import {createStore, applyMiddleware, compose} from 'redux';
import thunk from 'redux-thunk';
import {Provider} from 'react-redux';
import '@fortawesome/fontawesome-free/css/all.min.css';

import app from './reducers/app';
import {encodedOfferIdRE, getMixpanelId, getMyOffers, getVisitorId, isMeLanding, isNewLanding, isNewOffer, setMyOffers} from './util/util';
import mixpanel from 'mixpanel-browser';
import registerServices from './services/services';
import WhenIdle from '-/components/general/WhenIdle';
import Theme from '-/components/connected/Theme';
import SessionService from '-/services/SessionService';
/*
 * The event panel: `?debug=events` on a dev, test or QA build. See app/debug/enabled.js.
 *
 * The mode test is written INLINE rather than imported as a constant, deliberately. Vite
 * replaces `import.meta.env.MODE` with a string literal, so this reads `if ('production'
 * !== 'production' && ...)` in the production build and the bundler drops the branch, the
 * dynamic import, and the whole debug chunk with it. Importing the same check as a named
 * constant does NOT achieve that -- verified against a real `npm run build`, which still
 * emitted the chunk -- because the value is not propagated across the module boundary
 * into the guard.
 *
 * It taps the rails one microtask from now, so the synchronous `App Start` track below is
 * not captured. Everything after it is, which is the whole funnel.
 */
if (import.meta.env.MODE !== 'production') {
    import('-/debug/enabled')
        .then(({isDebugEnabled}) => isDebugEnabled() && import('-/debug').then(({install}) => install()))
        .catch(() => {});
}

const parsedQuery = qs.parse(location.search);
let {offerId, ...otherParams} = parsedQuery;

mixpanel.init(getMixpanelId(), {
    record_sessions_percent: 100
});

// The analytics identity is the encoded offer id when there is one. On the new-offer
// route (?offerId=new|cash|finance) and on the landing pages that start without an
// offer, there is not - and identifying by the literal "new" collapses every visitor on
// that route into a single Mixpanel profile. Fall back to a stable per-browser id, and
// alias it to the real offer id once createOffer mints one (see ApiService).
const hasEncodedOfferId = encodedOfferIdRE.test(offerId || '');
const analyticsId = hasEncodedOfferId ? offerId : getVisitorId();

mixpanel.identify(analyticsId);
mixpanel.register({
    analyticsId,
    hasOffer: hasEncodedOfferId,
    ...hasEncodedOfferId && {offerId}
});
mixpanel.people.set({
    'Offer Id': hasEncodedOfferId ? offerId : null,
    'Visitor Id': analyticsId
});
mixpanel.track('App Start', {hasOffer: hasEncodedOfferId});

// todo: Enable Webpack hot module replacement for reducers
// http://survivejs.com/webpack/advanced-techniques/configuring-react/

const INITIAL_STATE = window.INITIAL_STATE || {};
const store = createStore(
    app,
    INITIAL_STATE,
    compose(
        applyMiddleware(thunk.withExtraArgument({offerId})),
        window.devToolsExtension ? window.devToolsExtension() : f => f
    )
);

registerServices(store);

const FIFTEEN_MINUTES = 1000 * 60 * 15;

const render = () => {
    ReactDOM.render(
        <Provider store={store}>
            <WhenIdle timeLength={FIFTEEN_MINUTES}>
                <Theme />
            </WhenIdle>
        </Provider>,
        document.getElementById('app')
    );
};

const legacyOfferDiscovered = JSON.parse(localStorage.getItem('offerDiscovered')) || {};
const {offerId: discoveredOfferId = ''} = legacyOfferDiscovered;
const isCCRMode = SessionService.isCCRMode();
const isNew = isNewOffer();

let redirectToOffer = false;

if (!offerId && discoveredOfferId) {
    setMyOffers(discoveredOfferId);
    // remove old keys so we don't do it again
    localStorage.removeItem('offerDiscovered');

    const uniqueOfferIds = getMyOffers();

    if (uniqueOfferIds.length === 1) {
        // if we have only one offer id, let's assume it's the one we want to use
        ([offerId] = uniqueOfferIds);
        // make sure we redirect straight to the offer
        redirectToOffer = true;
    }
}

const offerIdIsValid = offerId && encodedOfferIdRE.test(offerId);
// might want to support more landing pages 
const isLanding = isMeLanding() || isNewLanding();

if (isLanding) {
    // noop...bypass the other checks since we can start without an offerId
} else if (!offerId || (!offerIdIsValid && !isNew)) {
    if (!isCCRMode) {
        const myOfferIds = getMyOffers();

        if (myOfferIds.length) {
            window.location.href = '/?t=me';
        } else if (window.isAcceptingApps) {
            if (window.defaultLandingPage === 'new') {
                window.location.href = '?t=new';
            } else if (window.defaultLandingPage === 'newOffer') {
                window.location.href = '?offerId=new';
            } else if (window.defaultLandingPage === 'discover') {
                window.location.href = '/discoverOffer/';
            }
        }
    }
} else if (offerId && redirectToOffer) {
    try {
        const params = qs.stringify(otherParams);
        const url = `/?offerId=${offerId}&${params}`;

        window.location.href = url;
    } catch(ex) {
        //
    }
}

const hasOfferId = offerId && (isNew || offerIdIsValid);

if (isLanding || hasOfferId || isCCRMode) {
    render();
    store.subscribe(render);
}
